import { Controller } from "@hotwired/stimulus";

/**
 * Relative Time Controller
 *
 * Renders a CDN-safe "Updated N min/hour/day ago" label from an ABSOLUTE
 * ISO8601 timestamp baked into the page (the data's last-changed time, which
 * moves in lockstep with the page's `fresh_when` ETag — never render time).
 *
 * Why absolute + client-relativize: a cached HTML copy is served to many
 * viewers over hours. A server-rendered relative string ("4 minutes ago")
 * would freeze into the cached bytes and lie. The absolute timestamp is
 * identical for every viewer of one copy; the relative phrase is computed
 * against each viewer's own clock, so it always shows the true age of the
 * bytes they were served.
 *
 * Markup:
 *   <time datetime="<ISO8601>"
 *         data-controller="relative-time"
 *         data-relative-time-datetime-value="<ISO8601>"
 *         title="<absolute local time>">…</time>
 */
// Refresh cadence: ~45s keeps "N min ago" honest without busy-looping.
const REFRESH_MS = 45_000;

export default class extends Controller<HTMLElement> {
  static values = { datetime: String };

  declare readonly datetimeValue: string;

  private timer: number | null = null;

  connect(): void {
    this.render();
    this.timer = window.setInterval(() => this.render(), REFRESH_MS);
  }

  disconnect(): void {
    // Clear the interval so a Turbo navigation away never leaks a timer.
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  private render(): void {
    const label = this.relativeLabel();
    if (label) this.element.textContent = label;
  }

  // "Updated 5 minutes ago" / "Updated 2 hours ago" / "Updated 3 days ago".
  // Returns null on a missing/invalid datetime so we never blank a stamp that
  // already rendered a sensible server fallback.
  private relativeLabel(): string | null {
    const raw = this.datetimeValue;
    if (!raw) return null;

    const then = Date.parse(raw);
    if (Number.isNaN(then)) return null;

    const diffMs = Date.now() - then;
    // Future timestamps (clock skew) read as "just now".
    const seconds = Math.max(0, Math.round(diffMs / 1000));

    let fmt: Intl.RelativeTimeFormat;
    try {
      fmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    } catch {
      return null;
    }

    let phrase: string;
    if (seconds < 60) {
      phrase = fmt.format(0, "minute"); // "this minute" / "now"
    } else if (seconds < 3_600) {
      phrase = fmt.format(-Math.floor(seconds / 60), "minute");
    } else if (seconds < 86_400) {
      phrase = fmt.format(-Math.floor(seconds / 3_600), "hour");
    } else {
      phrase = fmt.format(-Math.floor(seconds / 86_400), "day");
    }

    return `Updated ${phrase}`;
  }
}
